
<template>
    <div class="token-chart-container">
        <div class="chart-controls d-flex justify-content-end mb-2">
            <div class="btn-group btn-group-sm" role="group" aria-label="Chart Intervals">
                <button type="button" v-for="interval in intervals" :key="interval.value"
                        :class="['btn', currentInterval === interval.value ? 'btn-primary' : 'btn-outline-secondary']"
                        @click="changeInterval(interval.value)">
                    {{ interval.label }}
                </button>
            </div>
        </div>
        <div ref="chartContainer" class="chart-area"></div>
        <div v-if="isLoading" class="loading-overlay">Loading Chart...</div>
        <div v-if="error" class="error-overlay">{{ error }}</div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { createChart, CrosshairMode } from 'lightweight-charts';
import axios from 'axios';

const props = defineProps({
    mint: {
        type: String,
        required: true,
    }
});

const chartContainer = ref(null);
const chart = ref(null);
const candleSeries = ref(null);
const volumeSeries = ref(null);
const isLoading = ref(false);
const error = ref(null);
const isDarkMode = ref(false);

// Theme configurations
const themes = {
    light: {
        layout: {
            background: { color: '#ffffff' },
            textColor: '#333333',
        },
        grid: {
            vertLines: { color: '#e1e1e1' },
            horzLines: { color: '#e1e1e1' },
        },
        rightPriceScale: { borderColor: '#cccccc' },
        timeScale: { borderColor: '#cccccc' },
    },
    dark: {
        layout: {
            background: { color: '#1a1a2e' },
            textColor: '#e6edf3',
        },
        grid: {
            vertLines: { color: 'rgba(139, 92, 246, 0.1)' },
            horzLines: { color: 'rgba(139, 92, 246, 0.1)' },
        },
        rightPriceScale: { borderColor: 'rgba(139, 92, 246, 0.2)' },
        timeScale: { borderColor: 'rgba(139, 92, 246, 0.2)' },
    }
};

// Check if dark mode is active
const checkDarkMode = () => {
    return document.body.classList.contains('dark-mode') ||
           document.documentElement.classList.contains('dark-mode');
};

// Apply theme to chart
const applyTheme = () => {
    if (!chart.value) return;

    isDarkMode.value = checkDarkMode();
    const theme = isDarkMode.value ? themes.dark : themes.light;

    chart.value.applyOptions({
        layout: theme.layout,
        grid: theme.grid,
        rightPriceScale: theme.rightPriceScale,
        timeScale: {
            ...theme.timeScale,
            timeVisible: true,
            secondsVisible: false,
        },
    });
};

// Format time for crosshair tooltip
const formatCrosshairTime = (time) => {
    const date = new Date(time * 1000);
    return date.toLocaleString();
};

const intervals = [
    { label: '1m', value: '1m', range: 'now-6h' },
    { label: '5m', value: '5m', range: 'now-12h' },
    { label: '15m', value: '15m', range: 'now-1d' },
    { label: '1h', value: '1h', range: 'now-3d' },
    { label: '4h', value: '4h', range: 'now-7d' },
    { label: '1d', value: '1d', range: 'now-30d' },
    { label: '1w', value: '1w', range: 'now-90d' },
];
const currentInterval = ref(intervals[3].value); // Default to 1h (index 3)
const currentRange = ref(intervals[3].range);

let resizeObserver;
let themeObserver;

const initializeChart = () => {
    if (!chartContainer.value || chart.value) return;

    // Detect initial theme
    isDarkMode.value = checkDarkMode();
    const initialTheme = isDarkMode.value ? themes.dark : themes.light;

    chart.value = createChart(chartContainer.value, {
        width: chartContainer.value.clientWidth,
        height: chartContainer.value.clientHeight,
        layout: initialTheme.layout,
        grid: initialTheme.grid,
        crosshair: {
            mode: CrosshairMode.Normal,
        },
        rightPriceScale: initialTheme.rightPriceScale,
        timeScale: {
            ...initialTheme.timeScale,
            timeVisible: true,
            secondsVisible: false,
            tickMarkFormatter: (time, tickMarkType, locale) => {
                // time is in seconds (Unix timestamp)
                const date = new Date(time * 1000);
                // For short intervals, show time; for longer intervals, show date
                if (currentInterval.value === '1m' || currentInterval.value === '5m' || currentInterval.value === '15m') {
                    return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
                } else if (currentInterval.value === '1h' || currentInterval.value === '4h') {
                    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' }) +
                           ' ' + date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
                } else {
                    // Daily/weekly - show date only
                    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
                }
            },
        },
        localization: {
            timeFormatter: (time) => {
                const date = new Date(time * 1000);
                return date.toLocaleString();
            },
        },
    });

    candleSeries.value = chart.value.addCandlestickSeries({
        upColor: 'rgb(38,166,154)', // '#26a69a',
        downColor: 'rgb(239,83,80)', // '#ef5350',
        borderVisible: false,
        wickUpColor: 'rgb(38,166,154)', // '#26a69a',
        wickDownColor: 'rgb(239,83,80)', // '#ef5350',
        // Adjust price formatting for low-value tokens
        priceFormat: {
           type: 'price',
           precision: 8, // Increase precision (default is usually 2 or 4)
           minMove: 0.00000001, // Set minimum price movement
        },
    });

    volumeSeries.value = chart.value.addHistogramSeries({
        color: '#26a69a',
        priceFormat: {
            type: 'volume',
        },
        priceScaleId: 'volume_scale', // Use a separate price scale for volume
    });
    chart.value.priceScale('volume_scale').applyOptions({
        scaleMargins: {
            top: 0.7, // 70% space for price chart, 30% for volume
            bottom: 0,
        },
    });


    // Handle resizing
    resizeObserver = new ResizeObserver(entries => {
        const { width, height } = entries[0].contentRect;
        chart.value.applyOptions({ width, height });
    });
    resizeObserver.observe(chartContainer.value);

    fetchChartData();
};

const fetchChartData = async () => {
    isLoading.value = true;
    error.value = null;
    try {
        const response = await axios.get(`/api/tokens/${props.mint}/ohlcv`, {
            params: {
                interval: currentInterval.value,
                from: currentRange.value,
                to: 'now'
            }
        });

        const candleData = response.data.map(d => ({
            time: d.timestamp / 1000, // Lightweight Charts expects seconds timestamp
            open: d.open,
            high: d.high,
            low: d.low,
            close: d.close,
        })).filter(d => d.open !== null); // Filter out gaps if needed, or handle them

        const volumeData = response.data.map(d => ({
            time: d.timestamp / 1000,
            value: d.volume,
            color: d.open <= d.close ? 'rgba(38, 166, 154, 0.5)' : 'rgba(239, 83, 80, 0.5)', // Volume bar color based on candle
        })).filter(d => d.value !== null);

        if (candleSeries.value) {
            candleSeries.value.setData(candleData);
        }
        if (volumeSeries.value) {
            volumeSeries.value.setData(volumeData);
        }
        if (chart.value) {
            chart.value.timeScale().fitContent(); // Adjust timescale to fit data
        }

    } catch (err) {
        console.error('Error fetching chart data:', err);
        error.value = 'Failed to load chart data.';
    } finally {
        isLoading.value = false;
    }
};

const changeInterval = (interval) => {
    const selected = intervals.find(i => i.value === interval);
    if (selected) {
        currentInterval.value = selected.value;
        currentRange.value = selected.range;
        fetchChartData();
    }
};

onMounted(async () => {
    await nextTick(); // Ensure the container is rendered
    initializeChart();

    // Watch for theme changes on body and html elements
    themeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.attributeName === 'class') {
                applyTheme();
            }
        });
    });

    // Observe both body and html for class changes (theme toggle)
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
});

onUnmounted(() => {
    if (resizeObserver && chartContainer.value) {
        resizeObserver.unobserve(chartContainer.value);
    }
    if (themeObserver) {
        themeObserver.disconnect();
    }
    if (chart.value) {
        chart.value.remove();
        chart.value = null;
    }
});

// Optional: Refetch data if mint changes (e.g., in a dynamic context)
// watch(() => props.mint, fetchChartData);

</script>

<style scoped>
.token-chart-container {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 420px; /* Total height including controls */
}
.chart-controls {
    flex-shrink: 0;
}
.chart-area {
    flex: 1;
    min-height: 0; /* Important for flex children */
    width: 100%;
}
.loading-overlay, .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(255, 255, 255, 0.9);
    font-size: 1.2em;
    color: #333;
    backdrop-filter: blur(4px);
}
.error-overlay {
    color: #ef4444;
}

/* Dark mode support */
:global(.dark-mode) .loading-overlay,
:global(.dark-mode) .error-overlay {
    background-color: rgba(26, 26, 46, 0.9);
    color: #e6edf3;
}
:global(.dark-mode) .error-overlay {
    color: #ef4444;
}
</style>
